/**
 * Fixed-capacity ring buffer used to record a single coordinate channel
 * (one sample per integration step) for offline frequency analysis.
 */
export class ChannelRecorder {
  readonly cap: number;
  private readonly buf: Float64Array;
  private head = 0;
  private len = 0;

  constructor(cap: number) {
    this.cap = cap;
    this.buf = new Float64Array(cap);
  }

  push(v: number): void {
    this.buf[this.head] = v;
    this.head = (this.head + 1) % this.cap;
    if (this.len < this.cap) this.len++;
  }

  clear(): void {
    this.head = 0;
    this.len = 0;
  }

  get length(): number { return this.len; }

  copyInto(out: Float64Array): number {
    const n = Math.min(this.len, out.length);
    const start = (this.head - this.len + this.cap) % this.cap;
    for (let i = 0; i < n; i++) {
      out[i] = this.buf[(start + i) % this.cap];
    }
    return n;
  }
}

/** Iterative radix-2 Cooley–Tukey FFT. Length must be a power of two. */
function fftRadix2(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i]; re[i] = re[j]; re[j] = tr;
      const ti = im[i]; im[i] = im[j]; im[j] = ti;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = -2 * Math.PI / len;
    const wRe = Math.cos(ang);
    const wIm = Math.sin(ang);
    const half = len >> 1;
    for (let i = 0; i < n; i += len) {
      let curRe = 1;
      let curIm = 0;
      for (let k = 0; k < half; k++) {
        const a = i + k;
        const b = a + half;
        const vRe = re[b] * curRe - im[b] * curIm;
        const vIm = re[b] * curIm + im[b] * curRe;
        re[b] = re[a] - vRe;
        im[b] = im[a] - vIm;
        re[a] += vRe;
        im[a] += vIm;
        const nextRe = curRe * wRe - curIm * wIm;
        curIm = curRe * wIm + curIm * wRe;
        curRe = nextRe;
      }
    }
  }
}

function nextPow2Leq(n: number): number {
  let p = 1;
  while ((p << 1) <= n) p <<= 1;
  return p;
}

export interface SpectrumResult {
  freqs: Float64Array;
  magDb: Float64Array;
  sampleRate: number;
  binWidth: number;
  fftSize: number;
  decimation: number;
  peaks: Array<{ freq: number; db: number }>;
  period: PeriodEstimate;
}

/**
 * Compute a one-sided magnitude spectrum (in dB) from a uniformly sampled
 * real signal. The raw signal is block-averaged (decimated + crude low-pass)
 * toward a target sample rate so the band of interest for these pendula is
 * resolved without wasting bins on numerical noise near Nyquist.
 */
export function computeSpectrum(
  samples: Float64Array,
  len: number,
  dt: number,
): Omit<SpectrumResult, 'period'> | null {
  if (len < 64 || dt <= 0) return null;
  const rawFs = 1 / dt;
  const TARGET_FS = 64;
  const dec = Math.max(1, Math.round(rawFs / TARGET_FS));
  const fs = rawFs / dec;

  const decLen = Math.floor(len / dec);
  const N = nextPow2Leq(Math.min(decLen, 8192));
  if (N < 256) return null;

  const re = new Float64Array(N);
  const im = new Float64Array(N);

  let mean = 0;
  for (let i = 0; i < N; i++) {
    let s = 0;
    const base = i * dec;
    for (let j = 0; j < dec; j++) s += samples[base + j];
    re[i] = s / dec;
    mean += re[i];
  }
  mean /= N;

  const win = new Float64Array(N);
  let winSum = 0;
  for (let i = 0; i < N; i++) {
    const w = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));
    win[i] = w;
    winSum += w;
  }
  const norm = winSum > 0 ? 2 / winSum : 2 / N;
  for (let i = 0; i < N; i++) re[i] = (re[i] - mean) * win[i];

  fftRadix2(re, im);

  const half = N >> 1;
  const freqs = new Float64Array(half + 1);
  const magDb = new Float64Array(half + 1);
  let maxDb = -Infinity;
  for (let k = 0; k <= half; k++) {
    const mag = Math.sqrt(re[k] * re[k] + im[k] * im[k]) * norm;
    const db = 20 * Math.log10(mag + 1e-12);
    magDb[k] = db;
    freqs[k] = (k * fs) / N;
    if (db > maxDb) maxDb = db;
  }

  const peaks = findPeaks(freqs, magDb, maxDb);

  return {
    freqs, magDb, sampleRate: fs, binWidth: fs / N,
    fftSize: N, decimation: dec, peaks,
  };
}

function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/**
 * Fundamental frequency of a harmonic comb: treat the peak frequencies as
 * integer multiples of an unknown f0 and pick the f0 that makes them all land
 * (near) integers. Recovers the true fundamental even when it is itself too weak
 * to detect, and distinguishes a 0.125 Hz fundamental-with-odd-harmonics from a
 * 0.25 Hz line spacing. Returns null if the peaks are not a clean comb.
 */
function gcdFundamental(peaks: number[]): number | null {
  if (peaks.length === 0) return null;
  const fs = [...peaks].sort((a, b) => a - b);
  if (fs.length === 1) return fs[0];
  const fMin = fs[0];
  let best = 0, bestErr = Infinity;
  for (let k = 1; k <= 16; k++) {
    const f0 = fMin / k;
    if (f0 < 0.01) break;
    let err = 0;
    for (const f of fs) err += Math.abs(f / f0 - Math.round(f / f0));
    if (err < bestErr) { bestErr = err; best = f0; }
  }
  if (best <= 0 || bestErr / fs.length > 0.15) return null;
  return best;
}

export type PeriodQuality = 'periodic' | 'near-periodic' | 'quasiperiodic' | 'aperiodic';

export interface PeriodEstimate {
  /** Best-guess period in seconds; null only when there is no spectral comb at all. */
  period: number | null;
  /** Single-loop recurrence R̃(T)/R̃(0) in [0,1] — how well one period closes. */
  recurrence: number;
  /** How many consecutive loops stay coherent before decorrelating (window-limited). */
  cycles: number;
  quality: PeriodQuality;
}

/**
 * Graded orbit-period estimate from the FULL phase space (all recorded channels).
 *
 * Stage 1 — combined magnitude spectrum (sum of per-channel, energy-normalised
 * spectra) → comb fundamental f0 via the GCD test. All channels → robust to any
 * one coordinate sitting at a node.
 *
 * Stage 2 — combined autocorrelation R(τ) = Σᵢ ⟨xᵢ(t)·xᵢ(t+τ)⟩, window-corrected
 * to remove the finite-record overlap decay. The peak near T0 = 1/f0 is refined
 * by parabolic interpolation.
 *
 * This is a GRADED estimate, not a yes/no gate — built for near-periodic orbits
 * that loop a few times then drift. Whenever a comb is detectable it reports a
 * best-guess period plus two confidence signals:
 *   - recurrence: how completely a single loop closes (R̃(T)/R̃(0));
 *   - cycles: how many consecutive loops stay coherent (the decorrelation length).
 * Both are exactly what an orbit-refinement step would maximise. A signal with no
 * integer comb is labelled quasiperiodic (sharp incommensurate lines) or aperiodic.
 *
 * Both stages analyse the most-recent `windowSec` of the recording.
 */
export function computePeriod(
  channels: Float64Array[],
  len: number,
  dt: number,
  windowSec: number,
): PeriodEstimate {
  const none = (quality: PeriodQuality): PeriodEstimate =>
    ({ period: null, recurrence: 0, cycles: 0, quality });
  if (channels.length === 0 || len < 256 || dt <= 0) return none('aperiodic');

  // Both stages analyse the SAME most-recent window, so the comb fundamental and
  // the autocorrelation peak describe the same stretch of motion — essential for
  // non-stationary / drifting signals. Shorter window tracks drift but is noisier;
  // longer is more precise but assumes stationarity (and smears a drifting period).
  const NUSE = Math.min(len, Math.max(256, Math.floor(windowSec / dt)), 1 << 16);
  const start = len - NUSE;

  const rawFs = 1 / dt;
  const TARGET_FS = 64;
  const dec = Math.max(1, Math.round(rawFs / TARGET_FS));
  const fs = rawFs / dec;
  const decLen = Math.floor(NUSE / dec);
  const N = nextPow2Leq(Math.min(decLen, 8192));
  if (N < 256) return none('aperiodic');

  // ---- stage 1: combined magnitude spectrum -> comb fundamental ----
  const win = new Float64Array(N);
  let winSum = 0;
  for (let i = 0; i < N; i++) {
    win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));
    winSum += win[i];
  }
  const norm = winSum > 0 ? 2 / winSum : 2 / N;

  const combMag = new Float64Array((N >> 1) + 1);
  const re = new Float64Array(N);
  const im = new Float64Array(N);
  for (const ch of channels) {
    let mean = 0;
    for (let i = 0; i < N; i++) {
      let s = 0;
      const base = start + i * dec;
      for (let j = 0; j < dec; j++) s += ch[base + j];
      re[i] = s / dec;
      mean += re[i];
      im[i] = 0;
    }
    mean /= N;
    let energy = 0;
    for (let i = 0; i < N; i++) { re[i] = (re[i] - mean) * win[i]; energy += re[i] * re[i]; }
    if (energy <= 0) continue;
    const scale = norm / Math.sqrt(energy); // normalise each phase-space coordinate
    fftRadix2(re, im);
    const half = N >> 1;
    for (let k = 1; k <= half; k++) {
      combMag[k] += Math.sqrt(re[k] * re[k] + im[k] * im[k]) * scale;
    }
  }

  const fbin = fs / N;
  let maxMag = 0;
  for (let k = 1; k < combMag.length; k++) if (combMag[k] > maxMag) maxMag = combMag[k];
  const floor = maxMag * 0.06; // ~ -24 dB
  const combPeaks: number[] = [];
  for (let k = 2; k < combMag.length - 2; k++) {
    if (combMag[k] < floor) continue;
    if (combMag[k] > combMag[k - 1] && combMag[k] >= combMag[k + 1]) {
      const y0 = combMag[k - 1], y1 = combMag[k], y2 = combMag[k + 1];
      const d = y0 - 2 * y1 + y2;
      const delta = d !== 0 ? 0.5 * (y0 - y2) / d : 0;
      combPeaks.push((k + delta) * fbin);
    }
  }
  const f0 = gcdFundamental(combPeaks);

  // ---- stage 2: combined autocorrelation (raw, high-resolution, same window) ----
  const M = nextPow2(2 * NUSE);
  const ac = new Float64Array(M);
  const Re = new Float64Array(M);
  const Im = new Float64Array(M);
  for (const ch of channels) {
    let mean = 0;
    for (let i = 0; i < NUSE; i++) mean += ch[start + i];
    mean /= NUSE;
    let energy = 0;
    for (let i = 0; i < NUSE; i++) { Re[i] = ch[start + i] - mean; energy += Re[i] * Re[i]; Im[i] = 0; }
    if (energy <= 0) continue;
    const sc = 1 / Math.sqrt(energy);
    for (let i = 0; i < NUSE; i++) Re[i] *= sc;
    for (let i = NUSE; i < M; i++) Re[i] = 0;
    fftRadix2(Re, Im);
    for (let k = 0; k < M; k++) ac[k] += Re[k] * Re[k] + Im[k] * Im[k];
  }

  // inverse transform of the (real, symmetric) summed power -> R_total(tau)
  for (let k = 0; k < M; k++) { Re[k] = ac[k]; Im[k] = 0; }
  fftRadix2(Re, Im); // Re[tau] is now proportional to the combined autocorrelation

  // window-corrected autocorrelation: divide out the (NUSE-tau) overlap decay so a
  // truly periodic signal stays flat across loops and a drifting one decays for real.
  const rc = (tau: number): number => {
    if (tau < 0 || tau >= NUSE) return 0;
    return Re[tau] * (NUSE / (NUSE - tau));
  };
  const rc0 = rc(0);
  if (!(rc0 > 0)) return none('aperiodic');

  // Candidate periods (seconds): the GCD fundamental, plus integer multiples of
  // the inverse of each strong spectral peak. This covers the cases where the GCD
  // locked onto a harmonic or failed outright — the true first-return period is
  // among the candidates. We test them shortest-first and accept the first that
  // produces a coherent return, which gives the true (shortest) period.
  const candSet = new Set<number>();
  if (f0 != null && f0 > 0) candSet.add(1 / f0);
  for (const p of combPeaks.slice(0, 4)) {
    for (let n = 1; n <= 6; n++) candSet.add(n / p);
  }
  const cands = [...candSet].filter((t) => t > 0).sort((a, b) => a - b);

  const cap = Math.floor(0.85 * NUSE);
  let chosen: { tauT: number; recurrence: number; cycles: number } | null = null;
  let lastTc = -1;
  for (const Tc of cands) {
    const tauC = Tc / dt;
    if (tauC < 2 || tauC > cap) continue;
    if (lastTc > 0 && Math.abs(Tc - lastTc) < 0.03 * Tc) continue; // dedup near-duplicates
    lastTc = Tc;
    const lo = Math.max(2, Math.floor(0.85 * tauC));
    const hi = Math.min(NUSE - 2, Math.ceil(1.2 * tauC));
    if (hi <= lo) continue;
    let rMax = -Infinity, tauMax = -1;
    for (let t = lo; t <= hi; t++) {
      const v = rc(t);
      if (v > rMax) { rMax = v; tauMax = t; }
    }
    if (tauMax < 0) continue;
    const recur = Math.max(0, Math.min(1, rMax / rc0));
    if (recur < 0.4) continue; // not a genuine return at this lag

    const y0 = rc(tauMax - 1), y1 = rc(tauMax), y2 = rc(tauMax + 1);
    const dd = y0 - 2 * y1 + y2;
    const tauT = tauMax + (dd !== 0 ? 0.5 * (y0 - y2) / dd : 0);

    const maxPoss = Math.max(1, Math.floor(cap / Math.max(1, tauT)));
    let cyc = 0;
    for (let n = 1; n <= maxPoss + 1; n++) {
      const center = Math.round(n * tauT);
      if (center > cap) break;
      let best = -Infinity;
      for (let t = center - 2; t <= center + 2; t++) {
        const v = rc(t);
        if (v > best) best = v;
      }
      if (best / rc0 >= 0.4) cyc = n;
      else break;
    }
    if (cyc < 1) continue;

    chosen = { tauT, recurrence: recur, cycles: cyc };
    break; // shortest coherent return = the period
  }

  if (!chosen) return none(combPeaks.length >= 2 ? 'quasiperiodic' : 'aperiodic');

  const period = chosen.tauT * dt;
  const recurrence = chosen.recurrence;
  const cycles = chosen.cycles;
  const maxPossible = Math.max(1, Math.floor(cap / Math.max(1, chosen.tauT)));
  const clean = recurrence >= 0.5 && (cycles >= 5 || cycles >= maxPossible);
  const quality: PeriodQuality = clean ? 'periodic' : 'near-periodic';

  return { period, recurrence, cycles, quality };
}

function findPeaks(
  freqs: Float64Array,
  magDb: Float64Array,
  maxDb: number,
): Array<{ freq: number; db: number }> {
  const floor = maxDb - 60;
  const candidates: Array<{ freq: number; db: number }> = [];
  for (let k = 2; k < magDb.length - 2; k++) {
    if (magDb[k] < floor) continue;
    if (magDb[k] > magDb[k - 1] && magDb[k] >= magDb[k + 1]) {
      const y0 = magDb[k - 1], y1 = magDb[k], y2 = magDb[k + 1];
      const denom = y0 - 2 * y1 + y2;
      const delta = denom !== 0 ? 0.5 * (y0 - y2) / denom : 0;
      candidates.push({ freq: (k + delta) * freqs[1], db: y1 });
    }
  }
  candidates.sort((a, b) => b.db - a.db);
  const out: Array<{ freq: number; db: number }> = [];
  for (const c of candidates) {
    if (out.every((o) => Math.abs(o.freq - c.freq) > 0.15)) out.push(c);
    if (out.length >= 6) break;
  }
  return out;
}

/** Round a positive value up to a "nice" 1/2/5 × 10^k number for axis bounds. */
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const f = v / base;
  let nf: number;
  if (f <= 1) nf = 1;
  else if (f <= 2) nf = 2;
  else if (f <= 5) nf = 5;
  else nf = 10;
  return nf * base;
}

/** Produce evenly-spaced, round-number tick values spanning [0, axisMax]. */
function freqTicks(axisMax: number, target = 5): number[] {
  const rawStep = axisMax / target;
  const exp = Math.floor(Math.log10(rawStep));
  const base = Math.pow(10, exp);
  const f = rawStep / base;
  let nf: number;
  if (f < 1.5) nf = 1;
  else if (f < 3) nf = 2;
  else if (f < 7) nf = 5;
  else nf = 10;
  const step = nf * base;
  const out: number[] = [];
  for (let v = 0; v <= axisMax + step * 0.5; v += step) out.push(v);
  return out;
}

export interface SpectrumPanel {
  setChannels(labels: string[]): void;
  resize(w: number, h: number): void;
  refresh(): void;
  clear(): void;
}

export function createSpectrumPanel(opts: {
  channelSelect: HTMLSelectElement;
  computeBtn: HTMLButtonElement;
  copyBtn: HTMLButtonElement;
  liveToggle: HTMLInputElement;
  lookbackSlider: HTMLInputElement;
  lookbackValue: HTMLElement;
  canvas: HTMLCanvasElement;
  info: HTMLElement;
  getData: (channel: number) => { samples: Float64Array; len: number; dt: number } | null;
  getAllData: () => { channels: Float64Array[]; len: number; dt: number } | null;
}): SpectrumPanel {
  const { channelSelect, computeBtn, copyBtn, liveToggle, lookbackSlider, lookbackValue, canvas, info, getData, getAllData } = opts;
  const ctx = canvas.getContext('2d')!;
  let width = canvas.width;
  let height = canvas.height;
  let last: SpectrumResult | null = null;
  let lastChannel = -1;

  function currentChannel(): number {
    return Math.max(0, channelSelect.selectedIndex);
  }

  function currentLookback(): number {
    return Math.max(1, parseFloat(lookbackSlider.value) || 30);
  }

  function run(): void {
    const ch = currentChannel();
    const data = getData(ch);
    if (!data) {
      info.textContent = 'Not enough samples yet — let the simulation run.';
      last = null;
      draw();
      return;
    }
    const res = computeSpectrum(data.samples, data.len, data.dt);
    // period is a property of the whole orbit: estimate it from the full phase
    // space (all recorded channels) over the lookback window, independent of the
    // displayed channel.
    const all = getAllData();
    const period: PeriodEstimate = all
      ? computePeriod(all.channels, all.len, all.dt, currentLookback())
      : { period: null, recurrence: 0, cycles: 0, quality: 'aperiodic' };
    last = res ? { ...res, period } : null;
    lastChannel = ch;
    if (!res) {
      info.textContent = 'Not enough samples yet — let the simulation run.';
    }
    draw();
  }

  function draw(): void {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(244,240,228,1)';
    ctx.fillRect(0, 0, width, height);

    const padL = 40;
    const padR = 10;
    const padT = 18;
    const padB = 22;
    const plotW = width - padL - padR;
    const plotH = height - padT - padB;
    if (plotW <= 0 || plotH <= 0) return;

    if (!last) {
      ctx.fillStyle = 'rgba(133,96,49,0.7)';
      ctx.font = 'italic 400 13px "Cormorant Garamond", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Press \u201CCompute Spectrum\u201D after running the simulation',
        width / 2, height / 2);
      return;
    }

    const { freqs, magDb, sampleRate, binWidth, fftSize, decimation, peaks } = last;
    const fNyq = freqs[freqs.length - 1];
    let maxDb = -Infinity;
    let minDb = Infinity;
    for (let i = 1; i < magDb.length; i++) {
      if (magDb[i] > maxDb) maxDb = magDb[i];
      if (magDb[i] < minDb) minDb = magDb[i];
    }
    const top = Math.ceil(maxDb / 6) * 6;
    const bottom = Math.max(Math.floor((maxDb - 72) / 6) * 6, Math.floor(minDb));

    // adaptive x-axis: zoom onto the band holding the signal energy (within 40 dB of
    // the peak), so a slow pendulum's fundamental + harmonics fill the plot instead of
    // being crammed against the left edge of a 0..Nyquist axis.
    const thresh = maxDb - 40;
    let hiBin = 1;
    for (let k = freqs.length - 1; k >= 1; k--) {
      if (magDb[k] >= thresh) { hiBin = k; break; }
    }
    const fBand = Math.max(1.0, freqs[hiBin] * 1.25);
    const axisMax = Math.min(fNyq, niceCeil(fBand));
    const fTicks = freqTicks(axisMax);
    const fDecimals = axisMax < 2 ? 2 : axisMax < 10 ? 1 : 0;

    const xOf = (f: number) => padL + (f / axisMax) * plotW;
    const yOf = (db: number) =>
      padT + (1 - (db - bottom) / (top - bottom)) * plotH;

    ctx.strokeStyle = 'rgba(133,96,49,0.16)';
    ctx.lineWidth = 0.6;
    ctx.fillStyle = 'rgba(133,96,49,0.7)';
    ctx.font = '500 10px "Cormorant Garamond", Georgia, serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const dbSteps = Math.round((top - bottom) / 12);
    for (let i = 0; i <= dbSteps; i++) {
      const db = top - (i * (top - bottom)) / dbSteps;
      const y = yOf(db);
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(width - padR, y);
      ctx.stroke();
      ctx.fillText(`${db.toFixed(0)}`, padL - 4, y);
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const f of fTicks) {
      const x = xOf(f);
      ctx.fillText(f.toFixed(fDecimals), x, height - padB + 4);
    }

    ctx.strokeStyle = 'rgba(44,70,131,0.92)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    let started = false;
    for (let i = 1; i < freqs.length; i++) {
      if (freqs[i] > axisMax) break;
      const x = xOf(freqs[i]);
      const y = yOf(magDb[i]);
      if (!started) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    const labelColors = [
      'rgba(169,130,74,0.95)',
      'rgba(110,58,40,0.9)',
      'rgba(63,95,163,0.9)',
      'rgba(138,102,53,0.9)',
      'rgba(44,70,131,0.9)',
      'rgba(154,84,66,0.9)',
    ];
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.font = '500 11px "Cormorant Garamond", Georgia, serif';
    peaks.slice(0, 6).forEach((p, idx) => {
      if (p.freq > axisMax) return;
      const x = xOf(p.freq);
      const y = yOf(p.db);
      ctx.strokeStyle = labelColors[idx];
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, padT + plotH);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = labelColors[idx];
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillText(`${p.freq.toFixed(2)}Hz`, x + 4, y - 2);
    });

    // prominent period readout (graded, full-phase-space autocorrelation estimate)
    const pe = last.period;
    const qColor: Record<PeriodQuality, string> = {
      'periodic': 'rgba(44,70,131,0.95)',
      'near-periodic': 'rgba(133,96,49,0.95)',
      'quasiperiodic': 'rgba(110,58,40,0.9)',
      'aperiodic': 'rgba(120,116,100,0.85)',
    };
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    if (pe.period != null) {
      ctx.font = '600 14px "Cormorant Garamond", Georgia, serif';
      ctx.fillStyle = qColor[pe.quality];
      ctx.fillText(`T \u2248 ${pe.period.toFixed(3)} s`, padL, 2);
      ctx.font = 'italic 400 11px "Cormorant Garamond", Georgia, serif';
      ctx.fillStyle = 'rgba(133,96,49,0.78)';
      ctx.fillText(
        `f\u2080 \u2248 ${(1 / pe.period).toFixed(4)} Hz  \u00B7  recurrence ${(pe.recurrence * 100).toFixed(0)}%  \u00B7  ${pe.cycles} cycle${pe.cycles === 1 ? '' : 's'}  \u00B7  ${pe.quality}`,
        padL, 20,
      );
    } else {
      ctx.font = '600 13px "Cormorant Garamond", Georgia, serif';
      ctx.fillStyle = qColor[pe.quality];
      const msg = pe.quality === 'quasiperiodic'
        ? 'quasiperiodic \u2014 no single period'
        : 'aperiodic / chaotic';
      ctx.fillText(msg, padL, 4);
    }

    ctx.fillStyle = 'rgba(133,96,49,0.7)';
    ctx.font = '500 10px "Cormorant Garamond", Georgia, serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(
      `|X(f)| dB   fs\u2248${sampleRate.toFixed(1)}Hz  N=${fftSize}  \u0394f=${binWidth.toFixed(3)}Hz  dec\u00D7${decimation}`,
      width - padR, 4);

    const chTxt = lastChannel >= 0 ? channelSelect.options[lastChannel]?.text ?? '' : '';
    const pe2 = last.period;
    const periodTxt = pe2.period != null
      ? `T \u2248 ${pe2.period.toFixed(3)} s (recurrence ${(pe2.recurrence * 100).toFixed(0)}%, ${pe2.cycles} cycles, ${pe2.quality})`
      : (pe2.quality === 'quasiperiodic' ? 'quasiperiodic (no single period)' : 'aperiodic / chaotic');
    const peakTxt = peaks.length
      ? peaks.slice(0, 4).map((p) => `${p.freq.toFixed(3)}Hz`).join('   ')
      : 'no distinct peaks (broadband / chaotic?)';
    info.textContent = `${periodTxt} · channel ${chTxt} · `
      + `strongest: ${peaks.length ? peaks[0].freq.toFixed(3) + ' Hz' : '—'} · `
      + `peaks: ${peakTxt}`;
  }

  computeBtn.addEventListener('click', run);
  channelSelect.addEventListener('change', () => { if (last) run(); });
  lookbackSlider.addEventListener('input', () => {
    lookbackValue.textContent = `${lookbackSlider.value}s`;
  });
  lookbackSlider.addEventListener('change', () => { if (last) run(); });

  function peakReport(): string | null {
    if (!last) return null;
    const ch = lastChannel >= 0 ? channelSelect.options[lastChannel]?.text ?? '' : '';
    const lines: string[] = [];
    lines.push('Orbit Optimizer \u2014 frequency spectrum');
    lines.push(`Channel: ${ch}`);
    lines.push(`fs\u2248${last.sampleRate.toFixed(1)}Hz  N=${last.fftSize}  \u0394f=${last.binWidth.toFixed(3)}Hz  dec\u00D7${last.decimation}`);
    const pe = last.period;
    if (pe.period != null) {
      lines.push(`Period: ${pe.period.toFixed(4)} s   (f\u2080 = ${(1 / pe.period).toFixed(5)} Hz)`);
      lines.push(`Quality: ${pe.quality}   recurrence ${(pe.recurrence * 100).toFixed(0)}%   ${pe.cycles} coherent cycle${pe.cycles === 1 ? '' : 's'}   [full-phase-space autocorrelation, ${lookbackSlider.value}s lookback]`);
    } else {
      lines.push(`Period: \u2014 (${pe.quality})`);
    }
    if (last.peaks.length === 0) {
      lines.push('Peaks: none detected (broadband / chaotic)');
    } else {
      lines.push(`Strongest: ${last.peaks[0].freq.toFixed(3)} Hz`);
      lines.push('Peaks:');
      for (const p of last.peaks.slice(0, 6)) {
        lines.push(`  ${p.freq.toFixed(3)} Hz   ${p.db.toFixed(1)} dB`);
      }
    }
    return lines.join('\n');
  }

  function flashButton(msg: string): void {
    const orig = copyBtn.textContent;
    copyBtn.textContent = msg;
    setTimeout(() => { copyBtn.textContent = orig; }, 1200);
  }

  function fallbackCopy(text: string): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }

  copyBtn.addEventListener('click', async () => {
    const text = peakReport();
    if (!text) { flashButton('No data'); return; }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      fallbackCopy(text);
    }
    flashButton('Copied');
  });

  function setChannels(labels: string[]): void {
    channelSelect.innerHTML = '';
    for (const l of labels) {
      const o = document.createElement('option');
      o.textContent = l;
      channelSelect.appendChild(o);
    }
    channelSelect.disabled = labels.length === 0;
  }
  function resize(w: number, h: number): void {
    width = w; height = h;
    canvas.width = w; canvas.height = h;
    draw();
  }
  function refresh(): void {
    if (liveToggle.checked) run();
  }
  function clear(): void {
    last = null;
    lastChannel = -1;
    info.textContent = '';
    draw();
  }

  return { setChannels, resize, refresh, clear };
}
